import { useEffect, useState } from "react";
import {
  APIProvider,
  InfoWindow,
  Map,
  Marker,
} from "@vis.gl/react-google-maps";
import { sosDetailApi, updateSosSeenApi } from "api/sos";
import { GOOGLE_API_KEY } from "constants/common";
import { ISosPublicDetailResponse } from "models/sos";
import sosImage from "assets/images/sos.png";
import { useNotificationContext } from "contexts/NotificationContext";
import SosInfo from "./components/SosInfo";
import { useParams } from "react-router-dom";
import { Polyline } from "./components/polyline";
import { isDispatcher } from "helpers/privilege";

export default function SosDetail() {
  const { showError } = useNotificationContext();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<ISosPublicDetailResponse>();
  const [info, setInfo] = useState<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<{
    defaultCenter: { lat: number; lng: number };
  }>({
    defaultCenter: {
      lat: 13.496494229233356,
      lng: 39.47494624044569,
    },
  });

  useEffect(() => {
    // Validate ID before making API calls
    if (!id || id === "undefined" || id === "null") {
      showError("Invalid SOS ID. Please go back and try again.");
      return;
    }

    fetchSos();
    if (isDispatcher()) {
      updateSosSeenApi(id).then(() => { });
    }
  }, [id]); // eslint-disable-line

  const fetchSos = async () => {
    // Double check ID is valid before API call
    if (!id || id === "undefined" || id === "null") {
      showError("Invalid SOS ID");
      return;
    }

    const { data, error } = await sosDetailApi(id);
    if (data?.data) {
      data.data.locations.sort((a, b) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });
      setCurrentLocation((prev) => ({
        ...prev,
        defaultCenter: {
          lat: data!.data!.locations[data!.data!.locations.length - 1].latitude,
          lng: data!.data!.locations[data.data!.locations.length - 1].longitude,
        },
      }));
      setData(data.data);
    } else {
      showError(error!.message);
    }
  };

  const handleShowDriverInfo = () => setInfo(true);

  const handleCloseDriverInfo = () => setInfo(false);

  return (
    <div className="w-full p-2 h-[calc(100vh-72px)]">
      <APIProvider apiKey={GOOGLE_API_KEY}>
        <Map
          defaultZoom={16}
          {...currentLocation}
          style={{ width: "100vw", height: "100vh" }}
        >
          {data && (
            <>
              <Marker
                onClick={handleShowDriverInfo}
                key={data.id}
                icon={{
                  url: sosImage,
                  size: new google.maps.Size(80, 80),
                  origin: new google.maps.Point(0, 0),
                  anchor: new google.maps.Point(40, 40),
                  scaledSize: new google.maps.Size(80, 80),
                  rotation: 1,
                }}
                optimized
                position={currentLocation.defaultCenter}
              />
              {info && (
                <InfoWindow
                  headerDisabled
                  key={`${data.id}-info`}
                  position={currentLocation.defaultCenter}
                  onCloseClick={handleCloseDriverInfo}
                >
                  <SosInfo sos={data} />
                </InfoWindow>
              )}
            </>
          )}
          <Polyline
            path={
              data?.locations.map((value) => ({
                lat: value.latitude,
                lng: value.longitude,
              })) ?? []
            }
            strokeColor="#581845"
            strokeOpacity={1.0}
            strokeWeight={4}
          />
        </Map>
      </APIProvider>
    </div>
  );
}
